import React from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import SmoothImage from '@ff/ui/SmoothImage';
import { formatCurrency } from 'client/utils/formatter';
import { colors, H2, P12, P14, P16 } from 'client/v3/typography';
import Image from '@ff/ui/Image';
import Radio from '@ff/ui/Radio';
import UIError from 'client/utils/UIError';

export type OptionProps = {
  code: string;
  name: string;
  messages?: string[];
  productImageScaleRatio?: number;
  productImagePosition?: 'center' | 'top' | 'bottom' | 'left' | 'right';
  items: {
    code: string;
    image: string;
    productImage: string;
    title: string;
    features?: string[];
    price?: number;
    subOptions?: {
      code: string;
      title: string;
      price?: number;
    }[];
  }[],
  selectedItemCode: string;
  onOptionChanged: (optionCode: string, selectedItemCode: string) => void;
}

function Option(props: OptionProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    code,
    name,
    items,
    messages,
    productImageScaleRatio = 1,
    productImagePosition = 'center',
    onOptionChanged,
    selectedItemCode,
  } = props;

  if(!selectedItemCode){
    throw new UIError('The selectedItemCode is required.');
  }
  const selectedItem = items.find(i => i.code === selectedItemCode);
  const selectedSubOption = selectedItem?.subOptions?.find(so => so.code == selectedItemCode);
  const displayedPrice = formatCurrency(
    selectedSubOption ? selectedSubOption.price : selectedItem?.price
  );
  const subOptions = selectedItem?.subOptions?.map(so => ({ value: so.code, label: so.title })) || [];

  const handleOnClick = (optionName, selectedItemCode) => {
    onOptionChanged(optionName, selectedItemCode);
  }

  return (
    <Container ref={ref}>
      <ProductImageContainer>
        <ProductImage
          scaleRatio={productImageScaleRatio}
          objectPosition={productImagePosition}
          src={selectedItem?.productImage}
        />
      </ProductImageContainer>
      <H2>{name}</H2>
      <OptionItemsContainer>
        {items.map((i) => (
          <OptionItemImage
            key={ i.subOptions?.map(so => so.code).join(',') || i.code} // use codes in sub-options for key to prevent flurry
            selectedItemCode={selectedItemCode}
            itemCode={i.code}
            src={i.image}
            onClick={() => handleOnClick(code, i.code)}
          >
          </OptionItemImage>
        ))}
      </OptionItemsContainer>
      <P16>{selectedItem?.title}</P16>
      {messages?.map((msg) => (
        <OptionMessageContainer key={msg}>
          <Message>{msg}</Message>
        </OptionMessageContainer>
      ))}
      {!!selectedItem?.features?.length && (
        <FeaturesContainer>
          {selectedItem?.features?.map((f) => (
            <P14 fontWeight={300} key={f}>{f}</P14>
          ))}
        </FeaturesContainer>
      )}
      {subOptions.length > 0 &&
      (<SubOptionsContainer>
        {subOptions.map((so) => (
          <SubOptionContainer key={so.value}>
            <RadioButton
              name={selectedItemCode}
              id={so.value}
              label={so.label}
              checked={selectedItemCode === so.value}
              onChange={(e) => {
                if(e.target?.checked){
                  handleOnClick(code, so.value);
                }
              }}
            />
          </SubOptionContainer>
        ))}
      </SubOptionsContainer>)}
      {!!displayedPrice && <P16 marginTop='23px' fontWeight={300}>{displayedPrice}</P16>}
    </Container>
  );
}

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  textAlign: 'center',
  alignItems: 'center',
});

const FeaturesContainer = styled(Box)({
  marginTop: '23px',
});

const OptionItemsContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '20px',
  marginTop: '32px',
  marginBottom: '32px',
});

const OptionItemImage = styled(Image)<{itemCode: string, selectedItemCode: string}>(({itemCode, selectedItemCode}) => ({
  width: '56px',
  height: '56px',
  padding: '8px',
  border: itemCode === selectedItemCode ? colors.border4: 'none',
  cursor: 'pointer',
}));

const OptionMessageContainer = styled(Box)(({ theme }) => ({
  marginTop: '8px',
  textAlign: 'center',
  padding: '9px 16px',
  backgroundColor: colors.background2,

  marginLeft: '20px',
  marginRight: '20px',

  [theme.mediaQueries.md]: {
    marginLeft: '48px',
    marginRight: '48px',
  }
}));

const Message = styled(P12)({
  color: colors.message,
});

const SubOptionsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '23px',
  marginTop: '23px',
})

const SubOptionContainer = styled(Box)({
});

const RadioButton = styled(Radio)(({
  accentColor: colors.title,
  width: '22px',
  height: '22px',
  borderRadius: '50%',
  padding: '0px',

  ':hover': {
    cursor: 'pointer',
  },

  ':checked': {
    '+ label': {
      color: colors.title,
    }
  },

  '+ label': {
    fontSize: '14px',
    color: colors.content1,
  }
}));

const ProductImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',
  marginBottom: '36px',

  [theme.mediaQueries.md]: {
    display: 'none',
  }
}));

const ProductImage = styled(SmoothImage)<{ scaleRatio: number, objectPosition: string }>(({ scaleRatio, objectPosition }) => ({
  display: 'block',
  width: '100%',
  aspectRatio: '16/9',
  objectFit: scaleRatio > 1 ? 'contain' : 'cover',
  objectPosition,
  transform: `scale(${scaleRatio})`,
  backgroundColor: colors.background3,
}));

export default React.forwardRef<HTMLDivElement, OptionProps>(Option);